import type { ExecuteWorkflowMetadataType } from "@validereinc/domain";
import { toTitleCaseString } from "@validereinc/utilities";

const PENDING_APPROVAL_ENVELOPE_MARKER = "Tool execution requires approval";

const DEFAULT_WORKFLOW_TEMPLATE_REGISTRATION_STATUS = "active";
const DEFAULT_WORKFLOW_TEMPLATE_REGISTRATION_VERSION = 1;

/**
 * JSON shown on the execute-workflow approval card for `POST /v1/workflow_templates`.
 * Prefers agent-supplied `workflow_template_body`; otherwise reconstructs from legacy fields.
 */
export const buildWorkflowTemplateRegistrationBodyDisplay = (
  metadata: ExecuteWorkflowMetadataType
): Record<string, unknown> => {
  if (
    metadata.workflow_template_body !== undefined &&
    metadata.workflow_template_body !== null &&
    typeof metadata.workflow_template_body === "object" &&
    !Array.isArray(metadata.workflow_template_body)
  ) {
    return metadata.workflow_template_body as Record<string, unknown>;
  }

  const body: Record<string, unknown> = {
    name: metadata.name,
    config: metadata.config,
    status: DEFAULT_WORKFLOW_TEMPLATE_REGISTRATION_STATUS,
    version: DEFAULT_WORKFLOW_TEMPLATE_REGISTRATION_VERSION,
    triggers: [],
  };

  if (metadata.description !== undefined) {
    body.description = metadata.description;
  }

  if (metadata.category_id !== undefined) {
    body.category_id = metadata.category_id;
  }

  if (metadata.workflow_owner_id !== undefined) {
    body.workflow_owner_id = metadata.workflow_owner_id;
  }

  return body;
};

const PENDING_APPROVAL_TOOL_BOILERPLATE_PATTERN =
  /^Tool execution requires approval[\s\S]*\bTool:\s*\S+/i;

/**
 * Accompanying agent message before the stock HITL envelope in `content`.
 */
export const extractPendingApprovalAccompanyingAgentMessage = (
  content: string
): string | undefined => {
  const trimmed = content.trim();
  if (!trimmed) {
    return undefined;
  }

  const envelopeStartIndex = trimmed.indexOf(PENDING_APPROVAL_ENVELOPE_MARKER);
  if (envelopeStartIndex === -1) {
    return trimmed;
  }

  if (envelopeStartIndex === 0) {
    return undefined;
  }

  const accompanyingAgentMessage = trimmed.slice(0, envelopeStartIndex).trim();
  return accompanyingAgentMessage || undefined;
};

/**
 * Agent-generated HITL copy that duplicates structured `metadata`; omit from the feed.
 */
export const shouldSuppressPendingApprovalMessageContent = (
  content: string
): boolean => {
  const trimmed = content.trim();
  if (!trimmed) {
    return true;
  }

  if (extractPendingApprovalAccompanyingAgentMessage(trimmed)) {
    return false;
  }

  return PENDING_APPROVAL_TOOL_BOILERPLATE_PATTERN.test(trimmed);
};

/**
 * Markdown shown above the approval card (accompanying agent message only).
 */
export const getPendingApprovalAccompanyingAgentMessage = (
  content: string
): string | undefined => {
  const trimmed = content.trim();
  if (!trimmed) {
    return undefined;
  }

  const accompanyingAgentMessage =
    extractPendingApprovalAccompanyingAgentMessage(trimmed);
  if (accompanyingAgentMessage) {
    return accompanyingAgentMessage;
  }

  if (shouldSuppressPendingApprovalMessageContent(trimmed)) {
    return undefined;
  }

  return trimmed;
};

/**
 * Maps investigation write-tool API keys (e.g. `carbon_hub`) to a display label.
 */
export const formatInvestigationWriteApiDisplayName = (
  apiKey: string
): string => toTitleCaseString(apiKey, { intent: "ui" });

/**
 * Wraps arbitrary text in markdown inline-code fences, extending the fence when
 * the value contains backticks.
 */
export const formatMarkdownInlineCode = (raw: string): string => {
  let fence = "`";
  while (raw.includes(fence)) {
    fence += "`";
  }

  return `${fence}${raw}${fence}`;
};

const appendQueryParam = (
  searchParams: URLSearchParams,
  key: string,
  value: unknown
): void => {
  if (value === null || value === undefined) {
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item) => {
      appendQueryParam(searchParams, key, item);
    });
    return;
  }

  searchParams.append(key, String(value));
};

/**
 * Appends `params` as a query string to `path` for display in the approval card.
 */
export const formatWriteApiPathWithQueryParams = (
  path: string,
  params: Record<string, unknown> | null | undefined
): string => {
  if (!params || Object.keys(params).length === 0) {
    return path;
  }

  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    appendQueryParam(searchParams, key, value);
  }

  const queryString = searchParams.toString();
  if (!queryString) {
    return path;
  }

  const separator = path.includes("?") ? "&" : "?";
  return `${path}${separator}${queryString}`;
};
